
import { useState, useEffect, useCallback, RefObject } from 'react';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { useSessionMemory } from '@/hooks/useSessionMemory';

// Custom event for forcing overlay preview
export const CONTINUE_WATCHING_PREVIEW_EVENT = 'continue-watching-preview';

export function useContinueWatching(
  videoRef: RefObject<HTMLVideoElement>,
  features: VideoFeatures
) {
  const [showContinueWatchingOverlay, setShowContinueWatchingOverlay] = useState(false);
  const [savedPosition, setSavedPosition] = useState<number | null>(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  const {
    hasSessionData,
    sessionPosition,
    saveSessionPosition,
    clearSessionData,
    consumeSessionData
  } = useSessionMemory(features.videoId || '');

  // Check if continue watching is enabled in either format - STRICTER CHECK
  const continueWatchingEnabled = 
    (features.continueWatching?.enabled === true) ||
    (features.continueFromLastPosition === true);

  // Listen for preview event to force overlay display
  useEffect(() => {
    const handlePreviewEvent = () => {
      setIsPreviewMode(true);
      setShowContinueWatchingOverlay(true);
    };

    window.addEventListener(CONTINUE_WATCHING_PREVIEW_EVENT, handlePreviewEvent);
    
    return () => {
      window.removeEventListener(CONTINUE_WATCHING_PREVIEW_EVENT, handlePreviewEvent);
    };
  }, []);

  // Check if we're on the continue watching tab in personalization page
  const isContinueWatchingTab = () => {
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const tab = searchParams.get('tab');

    return (pathname.includes('/personalization') || pathname.includes('/personalizations'))
           && tab === 'continueWatching';
  };

  // Load saved position on mount
  useEffect(() => {
    if (!features.videoId) return;

    // If on continue watching tab and feature is enabled, show overlay as preview
    if (isContinueWatchingTab() && continueWatchingEnabled) {
      setSavedPosition(30); // Dummy position for preview
      setShowContinueWatchingOverlay(true);
      setIsPreviewMode(true);
      return;
    }

    // If continue watching is enabled, use the regular overlay
    if (continueWatchingEnabled) {
      const lastPosition = localStorage.getItem(`video-position-${features.videoId}`);
      if (lastPosition) {
        const position = parseFloat(lastPosition);
        if (position > 5) {
          setSavedPosition(position);
          setShowContinueWatchingOverlay(true);
        }
      }
    }
    // Session modal foi desabilitado - usar apenas ContinueWatchingOverlay
  }, [features.videoId, continueWatchingEnabled]);

  // Update overlay when on continue watching tab and settings change
  useEffect(() => {
    const checkAndUpdateOverlay = () => {
      if (isContinueWatchingTab()) {
        if (continueWatchingEnabled) {
          setSavedPosition(30); // Dummy position for preview
          setShowContinueWatchingOverlay(true);
          setIsPreviewMode(true);
        } else {
          setShowContinueWatchingOverlay(false);
          setIsPreviewMode(false);
        }
      } else {
        // Se não está na tab de continue watching, esconde o preview
        setShowContinueWatchingOverlay(false);
        setIsPreviewMode(false);
      }
    };

    // Check on every render when dependencies change
    checkAndUpdateOverlay();

    // Also check on a short interval to catch URL changes
    const intervalId = setInterval(checkAndUpdateOverlay, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [continueWatchingEnabled, features.continueWatchingOverlay?.template, features.continueWatchingOverlay?.useTemplate]);

  // Handle continue from saved position
  const handleContinue = () => {
    // In preview mode, just close the overlay
    if (isPreviewMode) {
      setShowContinueWatchingOverlay(false);
      setIsPreviewMode(false);
      return;
    }
    
    if (videoRef.current && savedPosition !== null) {
      videoRef.current.currentTime = savedPosition;
      videoRef.current.play().catch(err => console.error("Error playing video:", err));
    }
    setShowContinueWatchingOverlay(false);
  };

  // Handle restart from beginning
  const handleRestart = () => {
    // In preview mode, just close the overlay
    if (isPreviewMode) {
      setShowContinueWatchingOverlay(false);
      setIsPreviewMode(false);
      return;
    }
    
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => console.error("Error playing video:", err));
      
      // Clear saved position when explicitly choosing to restart
      if (features.videoId) {
        localStorage.removeItem(`video-position-${features.videoId}`);
      }
    }
    setShowContinueWatchingOverlay(false);
  };

  // Save position periodically (every 3 seconds)
  useEffect(() => {
    if (!features.videoId || !videoRef.current) return;
    
    const video = videoRef.current;
    let lastSaveTime = 0;
    
    const handleTimeUpdate = () => {
      if (!video.paused && video.currentTime > 5) {
        const now = Date.now();
        
        // Only save every 3 seconds to reduce writes
        if (now - lastSaveTime >= 3000) {
          // Save for continue watching ONLY if explicitly enabled
          if (continueWatchingEnabled) {
            localStorage.setItem(`video-position-${features.videoId}`, video.currentTime.toString());
          }
          // Always save session data for 24h memory feature
          saveSessionPosition(video.currentTime);
          lastSaveTime = now;
        }
      }
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [videoRef, features.videoId, continueWatchingEnabled, saveSessionPosition]);

  // Also save position when video pauses or page unloads
  useEffect(() => {
    if (!features.videoId || !videoRef.current) return;
    
    const video = videoRef.current;
    
    const saveCurrentPosition = () => {
      if (video.currentTime > 5) {
        // Save for continue watching ONLY if explicitly enabled
        if (continueWatchingEnabled) {
          localStorage.setItem(`video-position-${features.videoId}`, video.currentTime.toString());
        }
        // Always save session data
        saveSessionPosition(video.currentTime);
      }
    };
    
    const handlePause = () => saveCurrentPosition();
    
    // Save position on page unload/visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveCurrentPosition();
      }
    };
    
    video.addEventListener('pause', handlePause);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', saveCurrentPosition);
    
    return () => {
      video.removeEventListener('pause', handlePause);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', saveCurrentPosition);
    };
  }, [videoRef, features.videoId, continueWatchingEnabled, saveSessionPosition]);

  // Session modal handlers
  const handleSessionResume = () => {
    if (videoRef.current && sessionPosition !== null) {
      videoRef.current.currentTime = sessionPosition;
      videoRef.current.play().catch(err => console.error("Error playing video:", err));
    }
    setShowSessionModal(false);
    consumeSessionData();
  };

  const handleSessionRestart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => console.error("Error playing video:", err));
    }
    clearSessionData();
    setShowSessionModal(false);
  };

  const handleSessionCancel = () => {
    setShowSessionModal(false);
    consumeSessionData();
  };

  return {
    showContinueWatchingOverlay,
    setShowContinueWatchingOverlay,
    handleContinue,
    handleRestart,
    showSessionModal,
    sessionPosition,
    handleSessionResume,
    handleSessionRestart,
    handleSessionCancel,
    isPreviewMode
  };
}
